import { useState } from 'react';
import type { IAsset, ProviderRegistry } from '../providers';
import { ZapworksProvider } from '../providers/zapworks/ZapworksProvider';

export interface UseDeleteAssetsParams {
  registry: ProviderRegistry;
  onSuccess?: (successMessage: string) => void;
  onError?: (error: Error) => void;
  /** Fallback workspaceId used when the asset doesn't carry its own workspaceId */
  workspaceId?: string;
  /** Fired with each asset id that was successfully moved to the bin. */
  onMovedToBin?: (assetId: string) => void;
}

export function useDeleteAssets({ registry, onSuccess, onError, workspaceId: fallbackWorkspaceId, onMovedToBin }: UseDeleteAssetsParams) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const provider = registry.get('zapworks') as ZapworksProvider | undefined;

  /**
   * Resolve the workspace ID for an operation.
   * Prefer the asset's own workspaceId; fall back to the prop-level workspaceId.
   */
  const resolveWorkspaceId = (asset?: IAsset): string | undefined => {
    return asset?.getWorkspaceId() ?? fallbackWorkspaceId;
  };

  const deleteAsset = async (assetId: string, asset: IAsset): Promise<boolean> => {
    const successMessage = `${asset?.getName() ?? 'Asset'} was moved to Bin.`;

    if (!provider) {
      const err = new Error('Zapworks provider not found');
      setError(err.message);
      onError?.(err);
      return false;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await provider.delete(assetId, resolveWorkspaceId(asset));
      onMovedToBin?.(assetId);
      onSuccess?.(successMessage);
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete asset');
      setError(error.message);
      onError?.(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteMultiple = async (assetIds: string[], assets?: IAsset[]): Promise<boolean> => {
    const successMessage = `${assetIds.length} files were moved to Bin.`;

    if (!provider) {
      const err = new Error('Zapworks provider not found');
      setError(err.message);
      onError?.(err);
      return false;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await Promise.all(assetIds.map((id, i) => {
        const asset = assets?.find(a => a.getId() === id);
        return provider.delete(id, resolveWorkspaceId(asset)).then(() => {
          onMovedToBin?.(id);
        });
      }));
      onSuccess?.(successMessage);
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete assets');
      setError(error.message);
      onError?.(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteAsset, deleteMultiple, isDeleting, error };
}